import React from "react";
import { Box, CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Search } from "lucide-react";
import OpenHsModal from "../Items/OpenHsModal";
import { upperCaseInput } from "../../global/textCase";
import {
  globalStyle,
  handleDangerVisible,
  handleVisible,
} from "../../global/Utils";
import {
  getCompanyWithUser,
  getUserList,
  postProductionInvoice,
} from "../../Actions/UserAction";
import { SearchableSelect } from "../Common/SearchableSelect";

const PostDefaultProductionInvoice = () => {
  const dispatch = useDispatch();
  const {
    userList,
    isGettingUserList,
    isGettingCompanyForUser,
    companiesList,
    isSubmittingPostProductionInvoice,
  } = useSelector((state) => state.UserReducer);
  const hsCodeList = useSelector(
    (state) => state.HsCodeListReducer?.hsCodeList || [],
  );

  const [formData, setFormData] = React.useState({
    userId: "",
    companyId: "",
    HSCodeName: "",
    HSCodeId: "",
  });
  const [errors, setErrors] = React.useState({});
  const [openModal, setOpenModal] = React.useState(false);

  /* ------------------ HANDLE INPUT CHANGE ------------------ */
  const handleChange = (e) => {
    const { name, value } = upperCaseInput(e.target);
    if (name === "userId" && value) {
      dispatch(getCompanyWithUser(value));
    }
    if (name === "HSCodeName") {
      const hsCodeid = hsCodeList.find((item) => item.pctCode === value);
      setFormData((prev) => ({ ...prev, HSCodeId: hsCodeid?._id }));
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };
  /* ------------------ VALIDATION ------------------ */
  const validate = () => {
    const newErrors = {};
    const requiredFields = ["userId", "companyId", "HSCodeName"];
    requiredFields.forEach((field) => {
      const value = formData[field];
      if (!value || !value?.trim()) {
        newErrors[field] = "This field is required";
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ------------------ SUBMIT ------------------ */
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) {
      setTimeout(() => {
        const firstError = document.querySelector(".border-red-500");
        if (firstError) {
          firstError.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          firstError.focus();
        }
      }, 100);
      return;
    }
    const newFormData = Object.fromEntries(
      Object.entries(formData).filter(([key]) => key !== "HSCodeId"),
    );

    dispatch(
      postProductionInvoice(
        newFormData,
        setFormData,
        handleVisible,
        handleDangerVisible,
      ),
    );
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(getUserList("", "", "", ""));
    }, 500);
    return () => clearTimeout(timer);
  }, [dispatch]);

  return (
    <>
      {isGettingUserList ? (
        <Box
          sx={{
            position: "fixed", // ✅ covers full screen
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255,255,255,0.6)", // optional overlay
            zIndex: 9999,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <div className="w-full min-h-fit flex flex-col items-center py-4">
          <form
            onSubmit={handleSubmit}
            className="w-full space-y-6 mt-8"
          >
            <h2
              className="text-center font-bold text-xl"
              style={{
                fontFamily: globalStyle.fontFamilyHeading,
                color: globalStyle.curveDarkColor,
              }}
            >
              Post Default Production Invoice
            </h2>
            {/* GRID */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              {/* User Name */}
              <div>
                <label className="block mb-1 font-medium">
                  User Name<span className="text-red-500">*</span>
                </label>
                <SearchableSelect
                  name="userId"
                  value={formData?.userId}
                  onChange={handleChange}
                  disabled={!userList || userList.length === 0}
                  className={`w-full px-4 py-2 border rounded-md ${
                    errors.userId ? "border-red-500" : "border-gray-300"
                  }`}
                >
                  {!userList || userList.length === 0 ? (
                    <option value="">No Users Available</option>
                  ) : (
                    <>
                      <option value="">Select</option>
                      {userList.map((user) => (
                        <option key={user._id} value={user._id}>
                          {user.email}
                        </option>
                      ))}
                    </>
                  )}
                </SearchableSelect>
                {errors.userId && (
                  <p className="text-red-500 text-sm mt-1">{errors.userId}</p>
                )}
              </div>
              {/* Company Name */}
              {isGettingCompanyForUser ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "80px",
                  }}
                >
                  <CircularProgress size={30} />
                </Box>
              ) : (
                <div>
                  <label className="block mb-1 font-medium">
                    Company Name<span className="text-red-500">*</span>
                  </label>
                  <SearchableSelect
                    name="companyId"
                    value={formData?.companyId}
                    onChange={handleChange}
                    disabled={!companiesList || companiesList.length === 0}
                    className={`uppercase w-full px-4 py-2 border rounded-md ${
                      errors.companyId ? "border-red-500" : "border-gray-300"
                    }`}
                  >
                    {!companiesList || companiesList.length === 0 ? (
                      <option value="">No Companies Available</option>
                    ) : (
                      <>
                        <option value="">Select</option>
                        {companiesList.map((company) => (
                          <option key={company._id} value={company._id}>
                            {company.companyName} / {company?.ntn}
                          </option>
                        ))}
                      </>
                    )}
                  </SearchableSelect>
                  {errors.companyId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.companyId}
                    </p>
                  )}
                </div>
              )}
              {/* HS Code */}
              <div className="w-full">
                <label className="block mb-1 font-medium">
                  Hs Code<span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  <input
                    type="text"
                    name="HSCodeName"
                    value={formData.HSCodeName}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-md focus:outline-none ${
                      errors.HSCodeName
                        ? "border-red-500"
                        : "border-gray-300 focus:border-blue-500"
                    }`}
                  />
                  <Search
                    onClick={() => setOpenModal(true)}
                    size={30}
                    className="ml-2 mt-1 text-gray-500 cursor-pointer"
                  />
                </div>
                {errors.HSCodeName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.HSCodeName}
                  </p>
                )}
              </div>
            </div>
            {/* SUBMIT */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
              <button
                type="submit"
                disabled={isSubmittingPostProductionInvoice}
                className="w-full md:w-[40%] mx-auto block py-2 rounded-md text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-400"
                style={{ background: globalStyle.buttonColor }}
              >
                {isSubmittingPostProductionInvoice ? "Posting..." : "Post"}
              </button>
            </div>
          </form>
          <OpenHsModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            setFormData={setFormData}
          />
        </div>
      )}
    </>
  );
};

export default PostDefaultProductionInvoice;
